import { useEffect, useState } from "react";
import ImageUploader from "../../controllers/ImageUploader";
import { t } from "i18next";
import Notification from "../Notification";
import NEWAPI from "../../API/newIndex";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import { ArrowRight, Check, Copy, X } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { importProductUpdate } from "../../redux/thunk/productThunk";
import { adminSocket } from "../../utilities/socket";
import { parentNavigate } from "../../controllers/ParentNavigate";
import ButtonComponent from "../FormInput/ButtonComponent";
import InputComponent from "../FormInput/InputComponent";

const dataOptions = [
  // WooCommerce
  { title: "lang_Product_Categories", value: "product_categories" },
  { title: "lang_Products", value: "products" },
  { title: "lang_Coupons_Discounts", value: "coupons" },
  { title: "lang_Customers", value: "customers" },
  { title: "lang_Orders_Last_60_Days", value: "orders" },
  { title: "lang_Reviews", value: "reviews" },

  // WordPress
  { title: "lang_Pages", value: "pages" },
  { title: "lang_Posts", value: "posts" },
];

const WooMigrateDetailPage = () => {
  const { clientData } = useSelector((state) => state.default);
  const [migrationType, setMigrationType] = useState(null); // 'auth' | 'manual'
  const [selected, setSelected] = useState([
    "product_categories",
    "products",
    "coupons",
    "customers",
    "orders",
    "reviews",
    "pages",
    "posts",
  ]);
  const [csvFiles, setCsvFiles] = useState({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [isApiCalled, setIsApiCalled] = useState(false);
  const [showCredentialsInput, setShowCredentialsInput] = useState(false);
  const [credentials, setCredentials] = useState({
    consumer_key: "",
    consumer_secret: "",
    wp_username: "",
    wp_password: "",
    store_url: "",
    replace_content: false,
  });
  const [showHelp, setShowHelp] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [importStatus, setImportStatus] = useState({
    products: { loading: false, done: false, summary: null, error: null },
  });

  // handle input change
  const handleChangeCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!credentials.consumer_key || !credentials.consumer_secret || !credentials.store_url) {
      return;
    }
    localStorage.setItem("woo_migrate_payload", JSON.stringify(credentials));
    navigate("/woocommerce/migrate?from=woocommerce");
  };

  const handleChange = (item) => {
    setSelected((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  useEffect(() => {
    if (!adminSocket.connected) {
      adminSocket.connect();
    }

    const handleConnect = () => {
      console.log("Socket connected:", adminSocket?.id, clientData?._id);
      adminSocket.emit("connectClient", {
        client_id: clientData?._id?.toString(),
      });
    };

    const handleDisconnect = () => {
      console.log("Socket disconnected");
    };

    const handleConnectAdmin = (data) => {
      console.log("Admin socket connected:", data?.client_id, adminSocket?.id);
    };

    const handleImageProgress = (data) => {
      console.log("Image progress:", data);
      setMigrationType(null);
      setNotificationMsg(data.message);
      setSeverity(data.severity);
      setIsNotification(true);
    };

    adminSocket.on("connect", handleConnect);
    adminSocket.on("disconnect", handleDisconnect);
    adminSocket.on("connectAdmin", handleConnectAdmin);
    adminSocket.on("import-progress", handleImageProgress);

    return () => {
      adminSocket.off("connect", handleConnect);
      adminSocket.off("disconnect", handleDisconnect);
      adminSocket.off("connectAdmin", handleConnectAdmin);
      adminSocket.on("import-progress", handleImageProgress);
    };
  }, []);

  const runImport = async ({ key, apiUrl, fileUrl }) => {
    setImportStatus((prev) => ({
      ...prev,
      [key]: {
        loading: true,
        done: false,
        summary: null,
        error: null,
      },
    }));

    try {
      const res = await NEWAPI({
        url: apiUrl,
        method: "post",
        data: { url: fileUrl },
      });

      setImportStatus((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          done: true,
          summary: res.data,
          error: null,
        },
      }));
    } catch (err) {
      setImportStatus((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          done: true,
          summary: null,
          error: err,
        },
      }));
    }
  };

  const ManualWooCommerceImport = async () => {
    if (csvFiles?.products) {
      await runImport({
        key: "products",
        apiUrl: "/admin/product/ImportProductWordpress",
        fileUrl: csvFiles.products,
      });
    }
    setIsApiCalled(true);
  };

  const handleMigration = () => {
    if (migrationType === "manual") {
      if (!csvFiles) return;
      ManualWooCommerceImport();
    }
    if (migrationType === "auth") {
      localStorage.setItem("migrate_data", selected);
      setShowCredentialsInput(true);
    }
  };

  const replaceExtProducts = async () => {
    try {
      const payload = {
        url: csvFiles.products,
      };
      await dispatch(importProductUpdate(payload)).unwrap();

      setImportStatus((prev) => ({
        ...prev,
        products: {
          loading: false,
          done: true,
          summary: null,
          error: null,
        },
      }));
      setSeverity("success");
      setIsNotification(true);
      setNotificationMsg("Product Overrited Successfully");
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
  };

  const ImportSummary = ({ summary, type }) => {
    return (
      <div className="primary-bg-color p-2 rounded-[6px]">
        <div className="flex flex-col mt-2 gap-2">
          <div className="flex justify-between gap-2">
            <div className="flex items-center justify-center space-x-1 lowercase text-green-600 bg-green-200 text-sm rounded-[6px] px-4 py-2">
              <Check className="w-4" />
              <span>
                {t("lang_Successfully_Imported")}:<b>{summary?.insertedCount}</b>
              </span>
            </div>
            <div className="flex items-center justify-center space-x-1 lowercase text-black-600 bg-blue-200 text-sm rounded-[6px] px-4 py-2">
              <Copy className="w-4" />
              <span>
                {t("lang_Existing_Products")}:<b>{summary?.existingCount}</b>
              </span>
            </div>
            <div className="flex items-center justify-center space-x-1 lowercase text-red-600 bg-red-200 text-sm rounded-[6px] px-4 py-2">
              <X className="w-4" />
              <span>
                {t("lang_Failed_Imports")}:<b>{summary?.failedCount}</b>
              </span>
            </div>
            <div className="flex items-center justify-center space-x-1 lowercase text-red-600 bg-red-200 text-sm rounded-[6px] px-4 py-2">
              <X className="w-4" />
              <span>
                {t("lang_Failed_Image_Imports")}:<b>{summary?.failedImageCount}</b>
              </span>
            </div>
          </div>
          <hr className="mt-1" />

          {summary?.existingCount > 0 && (
            <>
              <div className="text-center">
                <span className="text-lx font-medium">
                  {t("lang_We_got")} <b>{summary?.existingCount}</b>{" "}
                  {t("lang_Existing_products_Details")}
                </span>
                {type === "products" && (
                  <div className="space-x-5 mt-2">
                    <button
                      className=" text-green-700 bg-green-200 hover:bg-green-300 text-lg rounded-[6px] px-2 py-1"
                      onClick={replaceExtProducts}
                    >
                      {t("lang_Yes")}
                    </button>
                    <button
                      className=" text-red-700 bg-red-200 hover:bg-red-300 text-lg rounded-[6px] px-2 py-1"
                      onClick={() =>
                        setImportStatus((prev) => ({
                          ...prev,
                          products: { ...prev.products, summary: null },
                        }))
                      }
                    >
                      {t("lang_No")}
                    </button>
                  </div>
                )}
              </div>
              <hr></hr>
            </>
          )}

          {summary?.failedCount > 0 && (
            <div className=" my-4">
              <span className="text-xl font-medium">
                {t("lang_Possible_Failed_Import_Reasons")}:
              </span>
              <Autocomplete
                disablePortal
                className="my-2 lowercase autocomplete"
                id="combo-box-demo"
                options={summary?.failedData}
                sx={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    {...params}
                    label="Failed products"
                  />
                )}
              />
              <ul className="pl-1">
                <li className="flex space-x-2 font-medium">
                  <ArrowRight className="w-4" />
                  <p>{t("lang_Not_proper_or_corrupted_csv_file")} </p>
                </li>
                <li className="flex space-x-2 font-medium">
                  <ArrowRight className="w-4" />
                  <p>{t("lang_InApropriate_title")}</p>
                </li>
                <li className="flex space-x-2 font-medium">
                  <ArrowRight className="w-4" />
                  <p>{t("lang_InApropriate_Variant_price_field")}</p>
                </li>
              </ul>
            </div>
          )}
          {summary?.failedImageCount > 0 && (
            <div className=" my-4">
              <span className="text-xl font-medium">
                {t("lang_Possible_Failed_image_import_reasons")}:
              </span>

              <ul className="pl-1">
                <li className="flex space-x-2 font-medium">
                  <ArrowRight className="w-4" />
                  <p>{t("lang_Not_proper_or_corrupted_csv_file")} </p>
                </li>
                <li className="flex space-x-2 font-medium">
                  <ArrowRight className="w-4" />
                  <p>{t("lang_InApropriate_image_file_extension")}</p>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };
  const statuses = Object.values(importStatus);

  const isAnyLoading = statuses.some((s) => s.loading);
  const isAnyError = statuses.some((s) => s.error);
  const activeKeys = Object.keys(csvFiles).filter((key) => Boolean(csvFiles[key]));
  const isAllDone =
    activeKeys.length > 0 &&
    activeKeys.every((key) => importStatus[key].done && !importStatus[key].loading);

  const options = [
    {
      value: "auth",
      title: t("lang_WooCommerce_Auth_Recommended"),
      recommended: true,
    },

    {
      value: "manual",
      title: t("lang_Manual_CSV_Upload"),
      recommended: false,
    },
  ];

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="flex items-baseline justify-center pt-5">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-md p-8 secondary-bg-color">
          {/* Title */}
          <h2 className="font-semibold mb-3 text-xl">
            {migrationType === "manual"
              ? t("lang_Manual_Store_Migration_WooCommerce_CSV_Upload")
              : t("lang_Migrate_Your_Store_from_WooCommerce")}
          </h2>

          {showCredentialsInput ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Consumer Key */}
              <InputComponent
                label={t("lang_Consumer_Key")}
                required
                type="text"
                name="consumer_key"
                placeholder="ck_xxxxxxxxxxxxxxxx"
                value={credentials.consumer_key}
                onChange={handleChangeCredentials}
              />

              {/* Consumer Secret */}
              <InputComponent
                label={t("lang_Consumer_Secret")}
                required
                type="text"
                name="consumer_secret"
                placeholder="cs_xxxxxxxxxxxxxxxx"
                value={credentials.consumer_secret}
                onChange={handleChangeCredentials}
              />

              {/* username */}
              <InputComponent
                label={t("lang_wp_username")}
                required
                type="text"
                name="wp_username"
                placeholder="xxxxxxxxxxxx"
                value={credentials.wp_username}
                onChange={handleChangeCredentials}
              />

              {/* username */}
              <InputComponent
                label={t("lang_wp_password")}
                required
                type="text"
                name="wp_password"
                placeholder="xxxxxxxxxxxx"
                value={credentials.wp_password}
                onChange={handleChangeCredentials}
              />

              {/* Store URL */}
              <InputComponent
                label={t("lang_Store_URL")}
                required
                type="text"
                name="store_url"
                placeholder="https://example.com"
                value={credentials.store_url}
                onChange={handleChangeCredentials}
              />

              {/* Help toggle */}
              <button
                type="button"
                onClick={() => setShowHelp(!showHelp)}
                className="text-blue-600 text-sm hover:underline"
              >
                {showHelp ? t("lang_Hide_Instructions") : t("lang_How_to_get_API_Keys")}
              </button>

              {/* Help Panel */}
              {showHelp && (
                <div className="bg-blue-50 border border-blue-200 text-sm text-blue-800 p-4 rounded-md space-y-2">
                  <p className="font-medium">{t("lang_Follow_these_steps")}</p>

                  <ol className="list-decimal ml-5 space-y-1">
                    <li>{t("lang_Login_to_WordPress_Admin")}</li>
                    <li>{t("lang_Go_to_WooCommerce_Settings")}</li>
                    <li>{t("lang_Open_Advanced_tab_REST_API")}</li>
                    <li>{t("lang_Click_Add_Key")}</li>
                    <li>{t("lang_Select_permission_Read_Write")}</li>
                    <li>{t("lang_Click_Generate_API_Key")}</li>

                    {/* NEW STEPS FOR WP USER AUTH */}
                    <li>{t("lang_Go_to_Users_Profile_section")}</li>
                    <li>{t("lang_Scroll_to_Application_Passwords")}</li>
                    <li>{t("lang_Enter_App_Name_and_Click_Generate")}</li>
                    <li>{t("lang_Copy_Application_Password")}</li>
                    <li>{t("lang_Use_Username_and_App_Password_for_API")}</li>
                  </ol>

                  <div className="pt-2">
                    <p>
                      <span className="font-semibold">{t("lang_Consumer_Key")}:</span>{" "}
                      {t("lang_starts_with")} <code>{t("lang_ck_")}</code>
                    </p>
                    <p>
                      <span className="font-semibold">{t("lang_Consumer_Secret")}:</span>{" "}
                      {t("lang_starts_with")} <code>{t("lang_cs_")}</code>
                    </p>
                    <p>
                      <span className="font-semibold">{t("lang_store_url")}: </span>
                      https://yourstore.com
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex items-center justify-between">
                <div
                  onClick={() => setShowCredentialsInput(false)}
                  className="w-fit flex text-link font-medium hover:underline text-sm items-center cursor-pointer"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-arrow-left w-3 mr-1"><g><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></g></svg> {t("lang_Go_back")}
                </div>

                <ButtonComponent
                  type="submit"
                  disabled={
                    !credentials.consumer_key ||
                    !credentials.consumer_secret ||
                    !credentials.store_url
                  }
                >
                  {t("lang_Submit")}
                </ButtonComponent>
              </div>
            </form>
          ) : (
            <>
              {!migrationType && (
                <div>
                  <h4 className="font-medium mb-3">{t("lang_Choose_Migration_Method")}</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {options.map((option) => {
                      return (
                        <label
                          key={option.value}
                          onClick={() => setMigrationType(option.value)}
                          className={`relative cursor-pointer rounded-xl border p-5 transition-all duration-300 main-border-color`}
                        >
                          {/* Recommended badge */}

                          {option.recommended && (
                            <span className="absolute top-3 left-3 bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                              Recommended
                            </span>
                          )}

                          {/* Title */}

                          <div className="mt-6 font-semibold text-[16px]">{option.title}</div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {migrationType === "auth" && (
                <>
                  {/* Description */}
                  <p className="mb-6 leading-relaxed">
                    {t(
                      "lang_This_migration_will_securely_transfer_your_store_data_from_your_woocommerce_store_to_our_platform_with_minimal_downtime",
                    )}
                  </p>

                  {/* Data List */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium mb-2">
                      {t("lang_Data_Included_in_Migration")}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {dataOptions.map((item) => (
                        <li key={item.value} className="flex items-center gap-2">
                          <Checkbox
                            checked={selected.includes(item.value)}
                            onChange={() => handleChange(item.value)}
                            size="small"
                          />
                          <span>{t(item.title)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Notice */}
                  <div className="mb-6 rounded-md border border-yellow-300 bg-yellow-50 p-4">
                    <p className="text-sm text-yellow-800 font-medium mb-1">
                      {t("lang_Important_Notice")}
                    </p>
                    <p className="text-sm text-yellow-700 leading-relaxed">
                      {t(
                        "lang_If_you_have_already_completed_a_migration_and_run_it_again_some_data_may_be_duplicated_or_replaced_dependin",
                      )}
                    </p>
                  </div>

                  {/* 🆕 Replace Content Checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                    <Checkbox
                      checked={credentials?.replace_content}
                      onChange={(e) =>
                        setCredentials({ ...credentials, replace_content: e.target.checked })
                      }
                      size="small"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t("lang_Replace_existing_product_content")}
                    </span>
                  </label>
                </>
              )}
              {migrationType === "manual" && (
                <div className="space-y-4 mb-6">
                  {[{ title: "Products", value: "products" }].map((item) => {
                    const fileUrl = csvFiles[item.value];
                    const status = importStatus[item.value];
                    if (isApiCalled && activeKeys.length > 0 && !activeKeys.includes(item.value))
                      return null;
                    return (
                      <div key={item.value} className="space-y-2">
                        <div className="w-full flex items-center justify-between border rounded-md p-3 main-border-color">
                          <div className="font-medium text-[15px] w-[30%]">{item.title}</div>
                          {typeof fileUrl === "string" && fileUrl !== "" ? (
                            <div className="w-[70%] flex justify-between items-center border border-dashed border-gray-300 rounded-md px-4 py-2 gap-2">
                              <p className="font-medium table-text">
                                {csvFiles[item.value]?.split("/").pop()}
                              </p>

                              <button
                                className="bulk-btn"
                                onClick={() =>
                                  setCsvFiles((prev) => ({ ...prev, [item.value]: null }))
                                }
                              >
                                {t("lang_IReplac_csv")}
                              </button>
                            </div>
                          ) : (
                            <ImageUploader
                              alreadyImage={fileUrl}
                              handleSetImage={(val) => {
                                if (!val || typeof val !== "string") return;

                                if (!val.toLowerCase().endsWith(".csv")) {
                                  setCsvFiles((prev) => ({ ...prev, [item.value]: null }));
                                  setIsNotification(true);
                                  setNotificationMsg("Please upload a CSV file.");
                                  setSeverity("error");
                                  return;
                                }

                                setCsvFiles((prev) => ({
                                  ...prev,
                                  [item.value]: val,
                                }));
                              }}
                              folder="productcsv"
                              format="csv"
                              multiple={false}
                              removeImages={() =>
                                setCsvFiles((prev) => ({ ...prev, [item.value]: null }))
                              }
                              className={
                                "flex items-center justify-between p-2 h-[90px] w-[140px] border-2 border-dashed border-gray-300 rounded-xl bg-white cursor-pointer"
                              }
                            />
                          )}
                        </div>
                        <div className="w-full mt-2">
                          {status.loading && (
                            <p className="text-sm text-blue-600">{t("lang_Importing")}</p>
                          )}

                          {!status.loading && status.error && (
                            <p className="text-sm text-red-600">
                              {t("lang_Import_failed_Please_check_the_CSV")}
                            </p>
                          )}

                          {!status.loading && status.summary && (
                            <ImportSummary summary={status.summary} type={item.value} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Action Button */}
              {migrationType && (
                <>
                  {isAllDone && !isAnyError ? (
                    <div className="flex items-center justify-end">
                      <ButtonComponent
                        onClick={() => {
                          setMigrationType(null);
                          setIsApiCalled(false);
                          setCsvFiles({});
                          parentNavigate("/dashboard");
                        }}
                      >
                        {t("lang_WooCommerce_Migration_Done")}
                      </ButtonComponent>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div
                        onClick={() => setMigrationType(null)}
                        className="w-fit flex font-medium hover:underline text-sm items-center cursor-pointer"
                      >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-arrow-left w-3 mr-1"><g><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></g></svg> {t("lang_Go_back")}
                      </div>

                      <ButtonComponent
                        loading={isAnyLoading}
                        onClick={handleMigration}
                      >
                        {isAnyLoading ? t("lang_Importing") : t("lang_Start_WooCommerce_Migration")}
                      </ButtonComponent>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default WooMigrateDetailPage;
